import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { Truck, Globe, ArrowRight } from "lucide-react";

const HERO_IMG = "/logistica-van.jpg";

const Logistica = () => {
  const { t } = useLang();

  return (
    <div data-testid="page-logistica">
      {/* Hero */}
      <section className="relative px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.3em] text-caramel mb-6 font-medium">
              {t.logistics.overline}
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] text-ink">
              {t.logistics.title}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-ink-muted font-light leading-relaxed max-w-2xl">
              {t.logistics.sub}
            </p>
          </div>
          <div className="lg:col-span-5 relative aspect-[5/4] hidden lg:flex items-center justify-center">
            <img src={HERO_IMG} alt="Furgoneta La Criollita" className="w-full h-full object-contain drop-shadow-2xl" />
          </div>
        </div>
      </section>

      {/* Two zones */}
      <section className="px-6 md:px-12 lg:px-20 pb-20">
        <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div data-testid="zone-national" className="bg-surface p-10 md:p-14 border border-bone">
            <Truck className="w-10 h-10 text-wine mb-8" strokeWidth={1} />
            <h3 className="font-serif text-3xl md:text-4xl font-light text-ink tracking-tight">
              {t.logistics.nationalTitle}
            </h3>
            <p className="mt-6 text-ink-muted font-light leading-relaxed">
              {t.logistics.nationalBody}
            </p>
          </div>
          <div data-testid="zone-eu" className="bg-ink text-bg p-10 md:p-14">
            <Globe className="w-10 h-10 text-caramel mb-8" strokeWidth={1} />
            <h3 className="font-serif text-3xl md:text-4xl font-light tracking-tight">
              {t.logistics.euTitle}
            </h3>
            <p className="mt-6 text-bone/80 font-light leading-relaxed">
              {t.logistics.euBody}
            </p>
          </div>
        </div>
      </section>

      {/* Zones table */}
      <section className="px-6 md:px-12 lg:px-20 py-20 md:py-28 bg-surface/40 border-t border-b border-bone">
        <div className="max-w-[1500px] mx-auto">
          <div className="text-[11px] uppercase tracking-[0.3em] text-caramel mb-6 font-medium">
            ⟶ {t.logistics.zonesTitle}
          </div>
          <h2 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-ink mb-12 leading-tight">
            {t.logistics.zonesTitle}
          </h2>

          <div className="border-t border-ink/30">
            {t.logistics.zones.map((z, i) => (
              <div
                key={i}
                data-testid={`zone-row-${i}`}
                className="grid grid-cols-12 gap-4 py-6 md:py-8 border-b border-ink/15 items-center hover:bg-bg/50 transition-colors group"
              >
                <div className="col-span-1 text-[11px] tracking-widest text-ink-muted">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-7 md:col-span-6 font-serif text-xl md:text-3xl text-ink font-light tracking-tight">
                  {z.z}
                </div>
                <div className="col-span-2 md:col-span-3 text-xs md:text-sm text-ink-muted uppercase tracking-widest">
                  {z.d}
                </div>
                <div className="col-span-2 text-right md:text-left text-xs md:text-sm font-medium text-wine">
                  {z.w}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wholesale CTA */}
      <section className="px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-ink leading-tight">
            {t.logistics.wholesaleCta}
          </h2>
          <Link
            to="/contacto"
            data-testid="wholesale-cta"
            className="mt-10 inline-flex items-center gap-3 bg-wine text-bg px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-wine-hover transition-colors group"
          >
            {t.logistics.wholesaleBtn}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Logistica;
